
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import UnstuckLogo from "@/components/UnstuckLogo";
import DiceRoller, { DiceRollerRef } from "@/components/DiceRoller";
import ContextualHelp from "@/components/help/ContextualHelp";
import { PromptStage } from "@/hooks/dice/useRollerState";
import { useSettings } from "@/context/SettingsContext";
import { getRpgThemeClasses } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Scroll, Sparkles } from "lucide-react";

interface JournalEntryViewProps {
  diceRollerRef: React.RefObject<DiceRollerRef>;
  onDiceRoll: (value: number) => void;
  onPromptStageChange: (stage: PromptStage) => void;
  hasDiceRolled?: boolean;
}

const JournalEntryView: React.FC<JournalEntryViewProps> = ({ 
  diceRollerRef, 
  onDiceRoll, 
  onPromptStageChange,
  hasDiceRolled = false
}) => {
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  return (
    <div className="space-y-4">
      <Card className={cn(
        "flex-1 relative",
        enableRpgMode ? (
          rpgTheme.cardBackground + " border-2 border-amber-600/50 shadow-lg"
        ) : ""
      )}>
        {enableRpgMode && (
          <div className="absolute inset-0 bg-gradient-to-br from-amber-50/10 to-amber-900/20 rounded-lg pointer-events-none" />
        )}
        
        <CardHeader className="relative z-10">
          <CardTitle className={cn(
            "flex items-center gap-2",
            enableRpgMode && rpgTheme.text + " font-bold drop-shadow-sm"
          )}>
            <UnstuckLogo textSize="sm" showTagline={false} />
            {enableRpgMode ? (
              <>
                <Sparkles className="h-5 w-5 text-amber-500" />
                <span>Cast the Sacred Dice</span>
              </>
            ) : (
              <span>Roll the Dice</span>
            )}
          </CardTitle>
          <CardDescription className={cn(
            enableRpgMode && rpgTheme.accent
          )}>
            {enableRpgMode 
              ? "Invoke the mystical dice to receive your quest prompts"
              : "Roll the dice to get journal prompts"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="relative z-10">
          <div aria-live="polite" aria-atomic="true">
            <DiceRoller 
              ref={diceRollerRef} 
              onDiceRoll={onDiceRoll} 
              onPromptStageChange={onPromptStageChange} 
              isJournalMode={true}
              hideWelcomeText={true}
              hasDiceRolled={hasDiceRolled}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contextual Help */}
      {!hasDiceRolled && (
        <ContextualHelp
          title="How Dice Rolling Works"
          content="Each number (1-6) corresponds to a different type of awareness prompt. The dice randomly selects which perspective to explore, helping you discover new insights about your stuck point."
          type="tip"
        />
      )}

      {hasDiceRolled && (
        <ContextualHelp
          title="Great! Now Complete Your Responses"
          content="Use the prompts that appeared to explore your stuck point from different angles. Take your time with each response - the more honest and detailed you are, the more helpful the process becomes."
          type="guidance"
        />
      )}
    </div>
  );
};

export default JournalEntryView;
